import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { NoticeService } from 'src/app/core/service/notice.service';
import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { OnlineUserGridRow, OnlineUserListResponse, OnlineUserSummary } from '../model/online-user-summary';
import { UserNotification } from '../model/user-notification';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-user-online',
  templateUrl: './user-online.component.html',
  styleUrls: ['./user-online.component.scss']
})
export class UserOnlineComponent
{
  @ViewChild('notificationControl') public notificationControl: any;

  @Output() public loading: EventEmitter<boolean> = new EventEmitter();
  @Output() public closing = new EventEmitter();

  public enableAdvanced = false;
  public pageSizes = [5, 10];
  public notificationForm: any;
  public notificationDialogOpened = false;
  public onlineUsersDialogOpened = false;
  public roles: Array<{ text: string; value: string }>;
  public userRoles: Array<{ text: string; value: string }>;
  public notificationTypes: Array<{ text: string; value: string }>;
  public photoMap: Map<string, string> = new Map();
  public totalOnlineUsers: number;
  public onlineView: GridViewManager;
  public selectedOnlineUsers = [];

  private dialogOpenedInput = false;
  private loadOnlineUsersInFlight = false;

  constructor(
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private fb: UntypedFormBuilder,
    private authenticationService: AuthenticationService,
    private noticeService: NoticeService,
    private userService: UserService)
  {
    this.roles = this.userService.roles;
    this.userRoles = sortBy(this.userService.roles, ['text']);
  }

  public get fromName(): UntypedFormControl
  {
    return this.notificationForm.get('fromName');
  }

  public get notification(): UntypedFormControl
  {
    return this.notificationForm.get('notification');
  }

  public get type(): UntypedFormControl
  {
    return this.notificationForm.get('type');
  }

  public get forceLogout(): UntypedFormControl
  {
    return this.notificationForm.get('forceLogout');
  }

  public get logoutDelay(): UntypedFormControl
  {
    return this.notificationForm.get('logoutDelay');
  }

  @Input() public set dialogOpened(value: boolean)
  {
    if (value === this.dialogOpenedInput)
    {
      return;
    }

    this.dialogOpenedInput = value;
    if (value)
    {
      this.notificationTypes = this.noticeService.notificationTypes;
      this.loadOnlineUsers();
    }
    else
    {
      this.onlineUsersDialogOpened = false;
    }
  }

  /**
   * Create notification form and open the modal.
   */
  public sendNotifications(): void
  {
    this.notificationForm = this.fb.group({
      fromName: [this.authenticationService.currentUser.name, Validators.required],
      notification: [null, Validators.required],
      type: ['warning', Validators.required],
      forceLogout: [false, Validators.required],
      logoutDelay: [60]
    });
    this.notificationDialogOpened = true;
    setTimeout(() => { this.notificationControl.nativeElement.focus(); });
  }

  /**
   * User selected and keeping selected ids.
   *
   * @param event Selected array keys
   */
  public onSelectedUserKeysChange(event: any): void
  {
    this.selectedOnlineUsers = event;
  }

  public getSelectedUserCount(): number
  {
    return this.selectedOnlineUsers.length;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public onlineUsersDataStateChange(state: DataStateChangeEvent): void
  {
    this.onlineView.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public onlineUsersGroupChange(groups: GroupDescriptor[]): void
  {
    this.onlineView.setGroup(groups);
  }

  /**
   * Get the Role localized text to display.
   *
   * @param dataItem User row item
   */
  public photoMapKey(dataItem: OnlineUserGridRow): string
  {
    return dataItem.username ?? String(dataItem.id);
  }

  public getRoleDisplayText(dataItem: Pick<OnlineUserSummary, 'role'>): string
  {
    const item = this.roles.find(obj => obj.value === dataItem.role);
    if (item)
    {
      return item.text;
    }
    else
    {
      return '';
    }
  }

  /**
   * Close online users dialog
   */
  public closeOnlineUsers(): void
  {
    this.onlineUsersDialogOpened = false;
    this.closing.emit();
  }

  /**
   * Close the notification dialog
   */
  public closeNotificationDialog(): void
  {
    this.notificationDialogOpened = false;
  }

  /**
   * Call back-end to send notifications to selected users.
   */
  public send(): void
  {
    // Build user notification object
    const userNotification: UserNotification = {
      fromName: this.fromName.value,
      notification: this.notification.value,
      type: this.type.value,
      forceLogout: this.forceLogout.value,
      logoutDelay: this.forceLogout.value ? this.logoutDelay.value : null,
      userIds: this.selectedOnlineUsers
    };
    this.noticeService.sendNotification(userNotification).subscribe({
      next: () =>
      {
        this.closeNotificationDialog();
        this.closeOnlineUsers();
        this.messageHandlerService.displaySuccess('NOTIFICATIONS-SENT-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.closeNotificationDialog();
        this.closeOnlineUsers();
      }
    });
  }

  /**
   * Load online users and show modal
   */
  private loadOnlineUsers(): void
  {
    if (this.loadOnlineUsersInFlight)
    {
      return;
    }

    this.loadOnlineUsersInFlight = true;
    this.loading.emit(true);
    this.selectedOnlineUsers = [];
    this.onlineView = new GridViewManager(5);
    this.onlineView.startLoading();
    this.userService.getAllOnlineUsers().subscribe({
      next: (value: OnlineUserListResponse) =>
      {
        this.photoMap = new Map();
        const rows: OnlineUserGridRow[] = value.data.map((user) => this.toOnlineUserGridRow(user));
        rows.forEach((row) => this.buildPhotoMapEntry(row));
        this.totalOnlineUsers = value.total;
        this.onlineView.setData(rows);
        this.onlineView.setSort([{ field: 'name', dir: 'asc' }]);
        this.onlineUsersDialogOpened = true;
      },
      error: (errorResponse) =>
      {
        this.loadOnlineUsersInFlight = false;
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.loadOnlineUsersInFlight = false;
        this.loading.emit(false);
        this.onlineView.stopLoading();
      }
    });
  }

  /**
   * Map OnlineUserSummaryDTO fields to a grid row (replaces legacy nested organization).
   */
  private toOnlineUserGridRow(user: OnlineUserSummary): OnlineUserGridRow
  {
    return {
      id: user.id,
      name: user.name,
      photo: user.photo,
      username: user.username,
      role: user.role,
      organizationName: user.organizationName ?? '',
      roleName: this.getRoleDisplayText(user)
    };
  }

  private buildPhotoMapEntry(row: OnlineUserGridRow): void
  {
    const key = this.photoMapKey(row);
    if (row.photo != null && row.photo !== '')
    {
      this.photoMap.set(key, 'data:image/jpeg;base64,' + row.photo);
      return;
    }

    const splitted = (row.name ?? '').split(' ');
    this.photoMap.set(
      key,
      splitted.length >= 2 ? `${splitted[0].charAt(0)}${splitted[1].charAt(0)}` : null);
  }
}
